import * as XLSXStyle from "xlsx-js-style";
import {
  headerStyle, textStyle, totalTextoStyle, totalMoneyStyle,
  celdaMoneda, celdaTexto, celdaNumero, celdaFecha, aNumero,
} from "./ExcelEstilos";
import { ESTADOS_PARTIDA, IMPORTANCIA, nombreProyecto, nombreUsuario } from "./ExcelRequisicion";

/**
 * Excel de RESUMEN de la descarga masiva de requisiciones. Dos hojas: `Resumen`
 * con un renglón por requisición y `Detalle` con un renglón por partida.
 *
 * El hermano para los documentos con CFDI es `ExcelDocumentosMasivo`; la
 * requisición no tiene `dataSat`, así que arma sus columnas desde el mismo
 * `values` que consume `requisicionPdf`.
 */

const COLUMNAS_ANCHAS = ["Descripcion", "Proyecto", "Solicito", "Observaciones", "Clave SAT"];
const anchoColumnas = (titulos) =>
  titulos.map((titulo) => (COLUMNAS_ANCHAS.includes(titulo) ? { wch: 30 } : { wch: 16 }));

const descripcionStyle = { ...textStyle, alignment: { vertical: "center", wrapText: true } };

const numeroRequisicion = (requisicion) =>
  [requisicion?.serie, requisicion?.numero].filter(Boolean).join(" ");

/** Suma de `cantidad * costo` de las partidas: lo que se estima gastar. */
const importeEstimado = (partidas) =>
  (partidas || []).reduce((total, partida) => total + aNumero(partida.cantidad) * aNumero(partida.costo), 0);

const hojaResumen = (documentos, modulo) => {
  const titulos = [
    "Numero", "Fecha", "Fecha de entrega", "Proyecto", "Solicito", "Importancia",
    "Partidas", "Cantidad", "Ordenada", "Pendiente", "Importe estimado", "Estado",
  ];

  const totales = { partidas: 0, cantidad: 0, ordenada: 0, importe: 0 };

  const filas = documentos.map((values) => {
    const requisicion = values?.data || {};
    const partidas = values?.insumos || [];

    const cantidad = partidas.reduce((total, partida) => total + aNumero(partida.cantidad), 0);
    const ordenada = partidas.reduce((total, partida) => total + aNumero(partida.cantidadOrdenada), 0);
    const importe = importeEstimado(partidas);

    totales.partidas += partidas.length;
    totales.cantidad += cantidad;
    totales.ordenada += ordenada;
    totales.importe += importe;

    return [
      celdaTexto(numeroRequisicion(requisicion)),
      celdaFecha(requisicion.createdAt),
      celdaFecha(requisicion.fechaEntrega),
      celdaTexto(nombreProyecto(values?.proyecto)),
      celdaTexto(nombreUsuario(values?.usuario)),
      celdaTexto(IMPORTANCIA[requisicion.importancia] || ""),
      celdaNumero(partidas.length),
      celdaNumero(cantidad),
      celdaNumero(ordenada),
      celdaNumero(cantidad - ordenada),
      celdaMoneda(importe),
      celdaTexto(modulo.etiquetaEstado(values)),
    ];
  });

  const indicePartidas = titulos.indexOf("Partidas");
  const indiceImporte = titulos.indexOf("Importe estimado");
  const filaTotales = titulos.map((titulo, indice) => {
    if (indice === 0) return celdaTexto("TOTAL", totalTextoStyle);
    if (indice === indicePartidas) return celdaNumero(totales.partidas, totalTextoStyle);
    if (indice === indicePartidas + 1) return celdaNumero(totales.cantidad, totalTextoStyle);
    if (indice === indicePartidas + 2) return celdaNumero(totales.ordenada, totalTextoStyle);
    if (indice === indicePartidas + 3) return celdaNumero(totales.cantidad - totales.ordenada, totalTextoStyle);
    if (indice === indiceImporte) return celdaMoneda(totales.importe, totalMoneyStyle);
    return celdaTexto("", totalTextoStyle);
  });

  const hoja = XLSXStyle.utils.aoa_to_sheet([
    titulos.map((titulo) => celdaTexto(titulo, headerStyle)),
    ...filas,
    filaTotales,
  ]);
  hoja["!cols"] = anchoColumnas(titulos);
  return hoja;
};

const hojaDetalle = (documentos) => {
  const titulos = [
    "Numero", "Fecha", "Proyecto", "Clave Int.", "Clave SAT", "Descripcion",
    "Unidad", "Cantidad", "Ordenada", "Pendiente", "Fecha entrega",
    "Costo unitario", "Importe estimado", "Observaciones", "Estado",
  ];

  const filas = [];
  for (const values of documentos) {
    const requisicion = values?.data || {};
    for (const partida of values?.insumos || []) {
      const cantidad = aNumero(partida.cantidad);
      const ordenada = aNumero(partida.cantidadOrdenada);
      const costo = aNumero(partida.costo);
      filas.push([
        celdaTexto(numeroRequisicion(requisicion)),
        celdaFecha(requisicion.createdAt),
        celdaTexto(nombreProyecto(values?.proyecto)),
        celdaTexto(partida.claveInt),
        celdaTexto(partida.claveSat),
        celdaTexto(partida.descripcion, descripcionStyle),
        celdaTexto(partida.unidad),
        celdaNumero(partida.cantidad),
        celdaNumero(partida.cantidadOrdenada || 0),
        celdaNumero(cantidad - ordenada),
        celdaFecha(partida.fechaEntrega),
        celdaMoneda(partida.costo),
        celdaMoneda(partida.costo ? cantidad * costo : ""),
        celdaTexto(partida.observaciones, descripcionStyle),
        celdaTexto(ESTADOS_PARTIDA[partida.estado] || ""),
      ]);
    }
  }

  const hoja = XLSXStyle.utils.aoa_to_sheet([
    titulos.map((titulo) => celdaTexto(titulo, headerStyle)),
    ...filas,
  ]);
  hoja["!cols"] = anchoColumnas(titulos);
  return hoja;
};

/**
 * @param {Object[]} documentos - los `values` (el mismo objeto que consume el PDF)
 * @param {Object} modulo - configuración del módulo (`etiquetaEstado`)
 * @returns {Blob} el .xlsx listo para descargar o meter al ZIP
 */
const construirExcelRequisiciones = (documentos, modulo) => {
  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, hojaResumen(documentos, modulo), "Resumen");
  XLSXStyle.utils.book_append_sheet(libro, hojaDetalle(documentos), "Detalle");

  const buffer = XLSXStyle.write(libro, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export default construirExcelRequisiciones;
